<template>
  <div>
    <div
      class="screen-overlay"
      :class="{'screen-overlay--visible': MenuOpen}"
      @click="toggleMenu()"
    />
    <div
      id="off-canvas-menu"
      :class="{'open': MenuOpen}"
      @click="toggleMenu()"
    >
      <font-awesome-icon
        :icon="['fal', 'bars']"
        class="icon alt"
      />
      <font-awesome-icon
        :icon="['fal', 'times']"
        class="icon alt"
      />
    </div>
    <transition name="slide">
      <div
        v-show="MenuOpen"
        class="off-canvas-menu__drawer"
      >
        <div
          class="off-canvas-menu__header"
        />
        <navigation-menu />
        <header-buttons id="mobile-user-menu" />
      </div>
    </transition>
  </div>
</template>

<script>
import NavigationMenu from '@/components/NavigationMenu.vue';
import HeaderButtons from '@/components/HeaderButtons.vue';

export default {
  name: 'OffCanvasMenu',
  components: {
    NavigationMenu,
    HeaderButtons,
  },
  data: () => ({
    MenuOpen: false,
  }),
  watch: {
    MenuOpen(MenuOpen) {
      if (MenuOpen) {
        document.documentElement.style.overflow = 'hidden';
        // document.documentElement is the same as using document.querySelector('#root')
      } else {
        document.documentElement.style.overflow = 'auto';
      }
    },
    $route() {
      this.MenuOpen = false;
    },
  },
  created() {
    document.addEventListener('keydown', this.closeMenuWithEsc);
  },
  destroyed() {
    document.removeEventListener('keydown', this.closeMenuWithEsc);
  },
  methods: {
    toggleMenu() {
      this.MenuOpen = !this.MenuOpen;
    },
    closeMenuWithEsc(e) {
      if (e.keyCode === 27 && this.MenuOpen) {
        this.MenuOpen = false;
      }
    },
  },
};
</script>

<style lang="scss">
  $header-height: 46px;

  #off-canvas-menu {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 10;
    display: block;
    width: 46px;
    height: 46px;
    cursor: pointer;

    svg {
      position: absolute;
      top: 8px;
      right: 11px;
      font-size: 28px;
      transform: rotate(0);
      transform-origin: center center;
      opacity: 1;
      transition: all 0.2s ease;

      &.fa-times {
        top: 8px;
        right: 14px;
        font-size: 30px;
        opacity: 0;
      }
    }

    &.open {
      svg {
        color: rgba($white, 0.7);
        transform: rotate(180deg);
        opacity: 0;

        &.fa-times {
          opacity: 1;

          &:hover {
            color: rgba($white, 0.9);
          }
        }
      }
    }
  }

  .off-canvas-menu__header {
    position: relative;
    height: $header-height;
    background: #0f2133;

    &__close-button {
      position: absolute;
      top: 22px;
      left: 16px;
      z-index: 3;
      color: rgba($white, 0.6);

      svg {
        font-size: 30px;
      }

      &:hover {
        color: white;
        cursor: pointer;
      }
    }
  }

  .off-canvas-menu__drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 9;
    width: 246px;
    height: 100vh;
    background: #1b3a59;
    box-shadow:
      0 14px 28px rgba($black, 0.25),
      0 10px 10px rgba($black, 0.22);

    ul {
      margin-bottom: 0;
      padding-left: 0;
      text-align: left;
      list-style: none;
    }

    li {
      font-size: 14px;
      border-bottom: 1px solid #183049;

      &:last-child {
        margin-bottom: 0.5rem;
      }
    }

    a {
      position: relative;
      display: block;
      width: 100%;
      height: $header-height;
      padding-left: 1.5rem;
      color: #e6e6e6;
      font-weight: 500;
      font-size: 15px;
      line-height: $header-height;
      letter-spacing: 0.02rem;

      &:hover {
        color: $green;
        background: #183049;
        cursor: pointer;
      }

      .fa-chevron-right {
        position: absolute;
        top: 20px;
        right: 15px;
        display: block;
        color: rgba($black, 0.6);
        font-size: 16px;
        opacity: 0.67;
      }
    }

    ::v-deep .header-buttons {
      padding: 15px;

      li {
        display: block;
        width: 100%;
        margin-bottom: 5px;
        border-bottom: none;

        button {
          display: block;
          width: 100%;
          padding: calc(#{$gutter} / 2);
        }
      }
    }

    ::v-deep .user-menu {
      position: fixed;
      bottom: 0;
      width: 300px;

      .dropdown.btn-group {
        width: 100%;
        border-left: 0;
        background: $dark-blue;
        outline: 0 !important;
        box-shadow: none !important;

        .dropdown-toggle {
          outline: 0 !important;
          box-shadow: none !important;

          &::after {
            transform: rotate(180deg);
          }
        }
      }

      .dropdown-menu {
        padding: 0;
        position: relative;
        z-index: 3;

        &::before {
          height: 100vh;
          background: rgba(black, 0.3);
          width: 300px;
          position: absolute;
          right: 0;
          bottom: 0;
          content: '';
          pointer-events: none;
          z-index: 1;
        }

        li {
          height: auto !important;
          min-height: 55px !important;
          background: #0f2133 !important;
          position: relative;
          z-index: 2;

          &:hover {
            background: darken(#0f2133, 5%) !important;
          }

          a {
            position: relative;
            display: block;
            width: 100%;
            height: 55px;
            padding-left: 15px;
            color: white !important;
            font-weight: bold;
            font-size: 15px !important;
            line-height: 55px !important;
            text-transform: uppercase;
            min-height: 55px !important;
            background: #0f2133 !important;

            &:hover {
              color: $green !important;
              background: darken(#0f2133, 5%) !important;
            }
          }

          i,
          svg {
            display: none;
          }
        }
      }
    }
  }

  .screen-overlay {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 8;
    width: 0;
    height: 0;
    background: $black;
    opacity: 0;
    transition: opacity 0.2s ease-out;

    &--visible {
      width: 100vw;
      height: 100vh;
      opacity: 0.7;
      transition: opacity 0.2s ease-out;
    }
  }

  .slide-enter-active,
  .slide-leave-active {
    transform: translateX(0);
    transition: all 0.2s ease-out;
  }

  .slide-enter,
  .slide-leave-to {
    transform: translateX(300px);
    transition: all 0.2s ease;
  }
</style>
